import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PermissionsContainer from '../../../components/PermissionsContainer';
import InvoiceOptionsDialog from '../../accounting/InvoiceOptionsDialog';
import { getInvoiceList } from '../../../services/invoiceList.service';

/**
 * Invoice Options, on the pro.
 *
 * V1 puts this link on the Invoicing tab beside the invoice number — on the main
 * invoice AND on each accessorial — and it opens the same screen the Invoice List
 * opens (main.pl:19443 `viewinvoiceoptions`). 360 had it only on the Invoice List, so
 * a CSR looking at the pro had to leave the pro to re-send a copy of its invoice.
 *
 * NOTHING IS REIMPLEMENTED HERE. The dialog is the accounting page's, in its
 * `existing` mode — view, print, save and e-mail an invoice that already exists, with
 * its backup paperwork. This file is the trigger, the permission gate, and the
 * "does the invoice exist yet" test; the Rebill/Credit modal already hosts the same
 * dialog from the pro side in `creditmemo` mode.
 *
 * IDENTITY IS THE HISTORY RECNO, NOT THE CHARGE. `cms.invoice.invhistoryrecno` names
 * the raised invoice; the charge row is only one of the things on it. Several pros
 * billed together share one history row, so opening this from pro X can legitimately
 * show an invoice covering pros X, Y and Z — that is the masterpro grouping, and it is
 * what the customer received.
 *
 * BEFORE THE INVOICE IS DATED it is Invoice Estimate Options instead: the same dialog in its
 * `estimate` mode, which is what the Invoice List's View Estimate opens (V1's
 * `$printtest`, a PDF named `<SAGE>-ESTIMATE`). One control, two states, one gate —
 * the same `invoice-options` grant covers both. The estimate is built from exactly the
 * rows the Invoice List would show for the order's masterpro + this bill-to: every
 * undated CMS charge on that masterpro billed to that customer, main invoice and
 * accessorials together, because that is what the invoice will be raised from.
 * It renders SAVED data, so it is disabled while the form holds unsaved edits.
 */
export const INVOICE_OPTIONS_COMPONENT_ID = 'invoice-options';
export const INVOICE_OPTIONS_COMPONENT_NAME = 'Pro Invoicing — Invoice Options';

interface Props {
  /** `cms.invoice.invhistoryrecno`. 0 until the invoice is raised. */
  invHistoryRecno: number;
  /** The invoice's own bill-to — recipients and grouping are read from it. */
  billto: number;
  /** Display invoice date. Blank until the invoice is invoiced; the control is
   *  offered only once it is set (Lee, 2026-10-21). */
  invoiceDate: string;
  /** invoice_history.masterpro — names the downloaded PDF and titles the dialog. */
  invoiceNumber?: string;
  /** Heading for the dialog, e.g. `Invoice 2639668` or `Accessorial 1 — Invoice …`. */
  title: string;
  /**
   * Stacking layer for the dialog's overlay. It is NOT a portalling component — it
   * renders a bare `fixed inset-0` div — so a host inside a modal has to name a layer
   * above that modal or the dialog paints behind the thing that opened it. The pro
   * slide-over is z-[100] and the Accessorial modal z-[130].
   */
  layerClassName?: string;
  /** Only one instance per page should carry the permissions-mode handle. */
  showHandle?: boolean;
  /** The order's masterpro. With a bill-to, it offers Invoice Estimate Options until the
   *  invoice is dated — it is how the Invoice List finds an invoice before it exists. */
  masterpro?: string;
  /** The host form has unsaved edits. The estimate renders saved data, so it waits. */
  unsaved?: boolean;
}

const InvoiceOptionsButton: React.FC<Props> = ({
  invHistoryRecno, billto, invoiceDate, invoiceNumber, title,
  layerClassName = 'z-[120]', showHandle = true, masterpro = '', unsaved = false,
}) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [estimate, setEstimate] = useState<{ recnos: number[]; billtoname: string } | null>(null);
  const [resolving, setResolving] = useState(false);

  // V1 offers the link only on an invoice that exists. Both halves of that are
  // checked: the DATE is what the user sees and what Lee asked the control to follow,
  // and the HISTORY RECNO is what the dialog is actually addressed by — without one
  // there is no invoice to view, print or send. They disagree on 10 of 1.2M live
  // invoice rows, all of them pre-port.
  const invoiced = invHistoryRecno > 0 && invoiceDate.trim() !== '';
  // Undated, and nothing half-raised either: those 10 disagreeing rows get neither.
  const estimable = !invoiceDate.trim() && invHistoryRecno <= 0 && billto > 0 && masterpro.trim() !== '';
  if (!invoiced && !estimable) return null;

  const openEstimate = async () => {
    setNote('');
    setResolving(true);
    try {
      const res = await getInvoiceList({ masterpro: masterpro.trim(), billto });
      const recnos = res.status === 'resolved' && res.target?.isNew
        ? res.rows.filter((r) => !r.invoiceDeleted).map((r) => r.invoicerecno)
        : [];
      if (recnos.length) setEstimate({ recnos, billtoname: res.target?.billtoname ?? '' });
      else setNote('Nothing to estimate — no uninvoiced CMS charges for this bill-to.');
    } catch {
      setNote('Could not load the estimate.');
    } finally {
      setResolving(false);
    }
  };

  return (
    <PermissionsContainer
      componentId={INVOICE_OPTIONS_COMPONENT_ID}
      componentName={INVOICE_OPTIONS_COMPONENT_NAME}
      defaultHidden
      showHandle={showHandle}
      className="inline-flex items-center gap-2"
    >
      {invoiced ? (
        <button
          type="button"
          onClick={() => { setNote(''); setOpen(true); }}
          className="text-[0.75rem] font-semibold text-blue-600 hover:underline dark:text-blue-400"
          title="View, print, save or e-mail this invoice"
        >
          Invoice Options
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void openEstimate()}
          disabled={unsaved || resolving}
          className="text-[0.75rem] font-semibold text-blue-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline dark:text-blue-400 dark:disabled:text-gray-500"
          title={unsaved
            ? 'Save your changes first — the estimate is built from the saved invoice'
            : 'View, print, save or e-mail the invoice these charges would produce — nothing is created'}
        >
          {resolving ? 'Invoice Estimate Options…' : 'Invoice Estimate Options'}
        </button>
      )}
      {note && (
        <span className="text-[0.71875rem] text-emerald-700 dark:text-emerald-400">{note}</span>
      )}
      {/* Portalled to <body>: a `position: fixed` descendant of a transformed element
          (the pro slide-over and both pro modals set transforms for dragging) is
          positioned and CLIPPED by that element rather than by the viewport. */}
      {open && createPortal(
        <InvoiceOptionsDialog
          mode={{ kind: 'existing', invoiceHistoryRecno: invHistoryRecno }}
          billto={billto}
          title={title}
          layerClassName={layerClassName}
          // The number exactly as the clerks say it, matching the archived PDF's own
          // name; the dialog falls back to the history recno when there is none.
          pdfName={invoiceNumber?.trim() || undefined}
          // `existing` mode creates nothing — the invoice is already there, so the
          // confirm step just hands its identity back.
          onConfirm={async () => [invHistoryRecno]}
          onClose={() => setOpen(false)}
          onDone={({ note: done }) => { setOpen(false); setNote(done || 'Done.'); }}
        />,
        document.body,
      )}
      {estimate && createPortal(
        <InvoiceOptionsDialog
          mode={{ kind: 'estimate', invoiceRecnos: estimate.recnos }}
          billto={billto}
          // Worded as the Invoice List words it, so the two read as one feature.
          title={`Invoice estimate — ${estimate.billtoname || title}`}
          layerClassName={layerClassName}
          // The Invoice List's estimate name: a bare masterpro would read as a raised invoice.
          pdfName={`invoice-estimate-${masterpro.trim()}`}
          // An estimate creates nothing; the dialog renders and sends the PDF itself.
          onConfirm={async () => []}
          onClose={() => setEstimate(null)}
          onDone={({ note: done }) => { setEstimate(null); setNote(done || 'Done.'); }}
        />,
        document.body,
      )}
    </PermissionsContainer>
  );
};

export default InvoiceOptionsButton;
